// Developer Platform console (admin-portal): platform-wide view of every
// tenant's API clients (auth-service via gateway). Read-only — clients are
// created/rotated/disabled from the tenant's own admin; a platform
// kill-switch API lands with partner management.
import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Col, Empty, Input, Row, Select, Space, Statistic, Table, Tag, Tooltip, Typography,
} from 'antd';
import {
  ApiOutlined, CheckCircleOutlined, ExperimentOutlined, ReloadOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import * as api from '../../services/developerService';

const { Title, Text } = Typography;

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

const usedInLast7Days = (c: api.DeveloperClient) =>
  !!c.lastUsedAt && Date.now() - new Date(c.lastUsedAt).getTime() <= SEVEN_DAYS_MS;

const environmentTag = (env: string) =>
  env === 'production' ? <Tag color="green">production</Tag> : <Tag color="blue">{env}</Tag>;

const statusTag = (status: string) =>
  status === 'active' ? (
    <Tag color="green">active</Tag>
  ) : status === 'disabled' ? (
    <Tag color="red">disabled</Tag>
  ) : (
    <Tag>{status}</Tag>
  );

// payroll:read is the sensitive scope (salary data) — highlighted amber.
const scopeTag = (scope: string) => (
  <Tag key={scope} color={scope === 'payroll:read' ? 'gold' : undefined}>{scope}</Tag>
);

const formatDate = (iso: string | null) =>
  iso ? fmtDateTime(iso) : null;

// ---------------------------------------------------------------------------
// Clients table columns
// ---------------------------------------------------------------------------
const columns: ColumnsType<api.DeveloperClient> = [
  {
    title: 'Tenant',
    dataIndex: 'tenantId',
    render: (tenantId: string) => (
      <Tooltip title={tenantId}>
        <Text copyable={{ text: tenantId }} style={{ maxWidth: 140 }} ellipsis>
          {tenantId}
        </Text>
      </Tooltip>
    ),
  },
  {
    title: 'Name',
    dataIndex: 'name',
    sorter: (a, b) => a.name.localeCompare(b.name),
  },
  {
    title: 'Client ID',
    dataIndex: 'clientId',
    render: (clientId: string) => <Text code copyable>{clientId}</Text>,
  },
  {
    title: 'Scopes',
    dataIndex: 'scopes',
    render: (scopes: string[]) => <Space size={[0, 4]} wrap>{scopes.map(scopeTag)}</Space>,
  },
  {
    title: 'Environment',
    dataIndex: 'environment',
    render: environmentTag,
  },
  {
    title: 'Status',
    dataIndex: 'status',
    render: statusTag,
  },
  {
    title: 'Rate limit',
    dataIndex: 'rateLimitPerMin',
    render: (n: number) => <Text>{n}/min</Text>,
  },
  {
    title: 'Last used',
    dataIndex: 'lastUsedAt',
    sorter: (a, b) =>
      (a.lastUsedAt ? new Date(a.lastUsedAt).getTime() : 0) -
      (b.lastUsedAt ? new Date(b.lastUsedAt).getTime() : 0),
    render: (iso: string | null) =>
      iso ? <Text>{formatDate(iso)}</Text> : <Text type="secondary">Never</Text>,
  },
  {
    title: 'Created',
    dataIndex: 'createdAt',
    sorter: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    render: (iso: string) => formatDate(iso),
  },
];

// ---------------------------------------------------------------------------
// Webhook health — cross-tenant delivery health (event-processor platform endpoint).
// ---------------------------------------------------------------------------
const WebhookHealthCard: React.FC = () => {
  const [h, setH] = useState<api.WebhookHealth | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'error'>('loading');
  useEffect(() => {
    api.getWebhookHealth()
      .then((d) => { setH(d); setState('ok'); })
      .catch(() => setState('error'));
  }, []);

  if (state === 'loading') {
    return <Card title="Webhook health"><Text type="secondary">Loading…</Text></Card>;
  }
  if (state === 'error' || !h) {
    return (
      <Card title="Webhook health">
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Webhook health unavailable." />
      </Card>
    );
  }
  return (
    <Card title="Webhook health — all tenants">
      <Row gutter={[16, 16]}>
        <Col xs={12} md={6}><Statistic title="Subscriptions" value={h.subscriptions.total} /></Col>
        <Col xs={12} md={6}><Statistic title="Failing" value={h.subscriptions.failing} valueStyle={h.subscriptions.failing ? { color: 'var(--color-warning)' } : undefined} /></Col>
        <Col xs={12} md={6}><Statistic title="Disabled" value={h.subscriptions.disabled} valueStyle={h.subscriptions.disabled ? { color: 'var(--color-danger)' } : undefined} /></Col>
        <Col xs={12} md={6}><Statistic title="Dead deliveries (7d)" value={h.deliveries.dead_7d} valueStyle={h.deliveries.dead_7d ? { color: 'var(--color-danger)' } : undefined} /></Col>
      </Row>
      {h.unhealthy_tenants.length > 0 && (
        <Table
          style={{ marginTop: 12 }}
          size="small"
          rowKey="tenant_id"
          pagination={false}
          dataSource={h.unhealthy_tenants}
          columns={[
            { title: 'Tenant', dataIndex: 'tenant_id', render: (t: string) => <Text code>{t}</Text> },
            { title: 'Failing', dataIndex: 'failing' },
            { title: 'Disabled', dataIndex: 'disabled' },
          ]}
        />
      )}
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export const DeveloperPlatformPage: React.FC = () => {
  const [clients, setClients] = useState<api.DeveloperClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [environmentFilter, setEnvironmentFilter] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    api.getPlatformClients()
      .then((data) => setClients(data.clients))
      .catch(() => { setClients([]); setError(true); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  // Filter options derived from the data itself, so new statuses/environments
  // show up without a frontend change.
  const statusOptions = useMemo(
    () => Array.from(new Set(clients.map((c) => c.status))).sort().map((s) => ({ value: s, label: s })),
    [clients],
  );
  const environmentOptions = useMemo(
    () => Array.from(new Set(clients.map((c) => c.environment))).sort().map((e) => ({ value: e, label: e })),
    [clients],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return clients.filter((c) => {
      if (statusFilter && c.status !== statusFilter) return false;
      if (environmentFilter && c.environment !== environmentFilter) return false;
      if (q && !c.name.toLowerCase().includes(q) && !c.clientId.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [clients, statusFilter, environmentFilter, search]);

  const stats = useMemo(
    () => ({
      total: clients.length,
      active: clients.filter((c) => c.status === 'active').length,
      sandbox: clients.filter((c) => c.environment === 'sandbox').length,
      usedLast7d: clients.filter(usedInLast7Days).length,
    }),
    [clients],
  );

  return (
    <div className="p-1">
      <Space align="center" className="mb-4" style={{ justifyContent: 'space-between', width: '100%' }}>
        <Space align="center">
          <ApiOutlined style={{ fontSize: 22 }} />
          <Title level={3} style={{ margin: 0 }}>Developer Platform</Title>
        </Space>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
          Refresh
        </Button>
      </Space>

      <Row gutter={[16, 16]} className="mb-4">
        <Col xs={12} md={6}>
          <Card>
            <Statistic title="API clients" value={stats.total} prefix={<ApiOutlined />} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Active"
              value={stats.active}
              valueStyle={{ color: 'var(--color-success)' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Sandbox"
              value={stats.sandbox}
              valueStyle={{ color: '#1677ff' }}
              prefix={<ExperimentOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Used in last 7 days"
              value={stats.usedLast7d}
              valueStyle={{ color: 'var(--color-warning)' }}
              prefix={<ThunderboltOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card title="API clients — all tenants">
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <Alert
                type="info"
                showIcon
                message="Disable a client from the tenant's own admin (platform kill-switch API lands with partner management)."
              />

              <Space wrap>
                <Select
                  allowClear
                  placeholder="Status"
                  style={{ minWidth: 140 }}
                  value={statusFilter}
                  onChange={setStatusFilter}
                  options={statusOptions}
                />
                <Select
                  allowClear
                  placeholder="Environment"
                  style={{ minWidth: 160 }}
                  value={environmentFilter}
                  onChange={setEnvironmentFilter}
                  options={environmentOptions}
                />
                <Input.Search
                  allowClear
                  placeholder="Search name or client ID"
                  style={{ width: 260 }}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Space>

              {error ? (
                <Alert
                  type="error"
                  showIcon
                  message="Unavailable"
                  description="Could not load API clients from auth-service."
                />
              ) : !loading && clients.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <Space direction="vertical" size={0}>
                      <Text strong>No API clients yet</Text>
                      <Text type="secondary">Tenants create API clients from their own admin.</Text>
                    </Space>
                  }
                />
              ) : (
                <Table
                  size="small"
                  rowKey="id"
                  loading={loading}
                  dataSource={filtered}
                  columns={columns}
                  pagination={{ pageSize: 20, showSizeChanger: true, hideOnSinglePage: true }}
                  scroll={{ x: true }}
                  locale={{
                    emptyText: (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="No clients match the current filters"
                      />
                    ),
                  }}
                />
              )}
            </Space>
          </Card>
        </Col>
        <Col span={24}>
          <WebhookHealthCard />
        </Col>
      </Row>
    </div>
  );
};

export default DeveloperPlatformPage;
